import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { ShipperGuideUserContext } from 'shipperguide/user/ui/UserContext'

import { type RemoteConfigAdapter } from '../domain/RemoteConfig'
import { UnleashAdapter } from '../infrastructure/UnleashAdapter'

type RemoteConfigContextValue = {
  ready: boolean
  adapter: RemoteConfigAdapter
}

const RemoteConfigContext = createContext<RemoteConfigContextValue | null>(null)
RemoteConfigContext.displayName = 'RemoteConfigContext'

const unleashAdapter = new UnleashAdapter()

type ConfigProviderProps = {
  adapter?: RemoteConfigAdapter
  children: ReactNode
}

export function ConfigProvider({ adapter = unleashAdapter, children }: ConfigProviderProps) {
  const [ready, setIsReady] = useState(adapter.ready)
  const shipperGuideUser = useContext(ShipperGuideUserContext)
  const user = shipperGuideUser?.user

  useEffect(
    function initAdapter() {
      adapter.init()
    },
    [adapter]
  )

  useEffect(
    function syncUserContext() {
      if (!user) return

      adapter.setContext({
        userId: user.uuid,
        properties: {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
        },
      })
    },
    [adapter, user]
  )

  useEffect(
    function handleAdapterReady() {
      const unsubscribeReady = adapter.subscribe('ready', () => {
        setIsReady(adapter.ready)
      })
      setIsReady(adapter.ready)

      return () => {
        unsubscribeReady()
      }
    },
    [adapter]
  )

  const value = useMemo(() => ({ ready, adapter }), [ready, adapter])

  return <RemoteConfigContext.Provider value={value}>{children}</RemoteConfigContext.Provider>
}

export function useRemoteConfig() {
  const context = useContext(RemoteConfigContext)
  if (!context) {
    throw new Error('useConfig must be used within ConfigProvider')
  }
  return context
}
